import React from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { waterfallData } from '../../data/mockData';

const WaterfallChart = () => {
  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(Math.abs(amount));
  };

  // Filter to show only channel allocations (spent type)
  const channelData = waterfallData.filter(d => d.type === 'spent');
  const maxValue = Math.max(...channelData.map(d => Math.abs(d.value)));

  return (
    <Card className="shadow-lg border-l-4 border-l-emerald-500">
      <CardHeader>
        <CardTitle className="text-xl font-semibold bg-gradient-to-r from-emerald-600 to-teal-600 bg-clip-text text-transparent">
          Budget Analysis
        </CardTitle>
      </CardHeader>
      <CardContent>
        {/* Chart Container with proper axis */}
        <div className="relative">
          {/* Y-axis labels */}
          <div className="absolute left-0 top-0 h-80 flex flex-col justify-between py-4 pr-4">
            <span className="text-xs text-gray-600">{formatCurrency(maxValue)}</span>
            <span className="text-xs text-gray-600">{formatCurrency(maxValue * 0.75)}</span>
            <span className="text-xs text-gray-600">{formatCurrency(maxValue * 0.5)}</span>
            <span className="text-xs text-gray-600">{formatCurrency(maxValue * 0.25)}</span>
            <span className="text-xs text-gray-600">$0</span>
          </div>

          {/* Chart area with proper alignment */}
          <div className="ml-16 relative">
            {/* Horizontal grid lines */}
            <div className="absolute inset-0 h-80 flex flex-col justify-between py-4">
              {[0, 0.25, 0.5, 0.75, 1].map((percentage, index) => (
                <div key={index} className="w-full border-t border-gray-200 opacity-50"></div>
              ))}
            </div>

            {/* Bars container */}
            <div className="h-80 flex items-end justify-between gap-4 py-4 relative z-10">
              {channelData.map((item, index) => {
                const height = (Math.abs(item.value) / maxValue) * 280;
                
                // Different colors for each channel
                let barColor = 'bg-gradient-to-t from-indigo-400 to-indigo-500';
                if (item.name === 'TV') {
                  barColor = 'bg-gradient-to-t from-indigo-400 to-indigo-500';
                } else if (item.name === 'Digital') {
                  barColor = 'bg-gradient-to-t from-violet-400 to-violet-500';
                } else if (item.name === 'Print') {
                  barColor = 'bg-gradient-to-t from-amber-400 to-amber-500';
                } else if (item.name === 'Radio') {
                  barColor = 'bg-gradient-to-t from-orange-400 to-orange-500';
                } else if (item.name === 'Outdoor') {
                  barColor = 'bg-gradient-to-t from-emerald-400 to-emerald-500';
                }

                return (
                  <div key={index} className="flex-1 flex flex-col items-center max-w-20">
                    <div className="relative group w-full">
                      <div 
                        className={`w-full ${barColor} rounded-t-lg shadow-lg hover:shadow-xl transition-all duration-300 cursor-pointer border-2 border-white`}
                        style={{ height: `${height}px`, minHeight: '8px' }}
                      >
                        {/* Value label on hover */}
                        <div className="absolute -top-16 left-1/2 transform -translate-x-1/2 opacity-0 group-hover:opacity-100 transition-opacity duration-200 z-20">
                          <div className="bg-gray-900 text-white text-xs px-3 py-2 rounded-lg whitespace-nowrap shadow-lg">
                            <div className="font-semibold">{item.name}</div>
                            <div>{formatCurrency(item.value)}</div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
            
            {/* X-axis line */}
            <div className="w-full border-t-2 border-gray-300 mt-0"></div>
            
            {/* Labels and amounts below X-axis */}
            <div className="flex justify-between gap-4 mt-4">
              {channelData.map((item, index) => (
                <div key={index} className="flex-1 text-center max-w-20">
                  <div className="text-sm font-medium text-gray-900 mb-1 leading-tight">{item.name}</div>
                  <div className="text-xs text-gray-600">{formatCurrency(item.value)}</div>
                  <div className="text-xs text-gray-500 mt-1">(Allocated)</div>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Summary Cards */}
        <div className="grid grid-cols-3 gap-4 text-sm mt-8 pt-6 border-t border-gray-200">
          <div className="text-center p-4 bg-indigo-50 rounded-lg border border-indigo-200">
            <div className="text-lg font-bold text-indigo-600">
              {formatCurrency(waterfallData.find(d => d.type === 'planned')?.value || 0)}
            </div>
            <div className="text-indigo-700 font-medium">Total Planned</div>
          </div>
          <div className="text-center p-4 bg-red-50 rounded-lg border border-red-200">
            <div className="text-lg font-bold text-red-600">
              {formatCurrency(waterfallData.filter(d => d.type === 'spent').reduce((sum, d) => sum + Math.abs(d.value), 0))}
            </div>
            <div className="text-red-700 font-medium">Total Spent</div>
          </div>
          <div className="text-center p-4 bg-emerald-50 rounded-lg border border-emerald-200">
            <div className="text-lg font-bold text-emerald-600">
              {formatCurrency(waterfallData.find(d => d.type === 'remaining')?.value || 0)}
            </div>
            <div className="text-emerald-700 font-medium">Remaining Budget</div>
          </div>
        </div>

        {/* Budget Allocation Recommendations as Horizontal Bars */}
        <div className="mt-6 space-y-4">
          <h4 className="text-lg font-semibold text-gray-900 mb-4">Budget Allocation Recommendations & Actionable Insights</h4>
          
          <div className="space-y-4">
            {/* Recommendation 1 - Horizontal Bar */}
            <div className="bg-gradient-to-r from-emerald-50 to-green-50 border border-emerald-200 rounded-lg p-4">
              <div className="flex items-center justify-between mb-3">
                <div>
                  <h5 className="font-semibold text-emerald-800">Increase Digital Allocation</h5>
                  <p className="text-sm text-emerald-700">Shift $600K from Print to Digital (+12.5% allocation)</p>
                </div>
                <div className="text-right">
                  <div className="text-lg font-bold text-emerald-600">+28%</div>
                  <div className="text-xs text-emerald-600">ROI Uplift</div>
                </div>
              </div>
              <div className="w-full bg-emerald-200 rounded-full h-3 mb-2">
                <div className="bg-gradient-to-r from-emerald-400 to-emerald-500 h-3 rounded-full" style={{ width: '85%' }}></div>
              </div>
              <div className="flex justify-between text-xs text-emerald-700">
                <span><strong>Impact:</strong> +$1.8M revenue | <strong>Timeline:</strong> Q2 2025</span>
                <span>85% Priority</span>
              </div>
            </div>

            {/* Recommendation 2 - Horizontal Bar */}
            <div className="bg-gradient-to-r from-blue-50 to-indigo-50 border border-blue-200 rounded-lg p-4">
              <div className="flex items-center justify-between mb-3">
                <div>
                  <h5 className="font-semibold text-blue-800">Optimize TV Investment</h5>
                  <p className="text-sm text-blue-700">Renegotiate rates & shift to prime time slots</p>
                </div>
                <div className="text-right">
                  <div className="text-lg font-bold text-blue-600">-25%</div>
                  <div className="text-xs text-blue-600">Cost Reduction</div>
                </div>
              </div>
              <div className="w-full bg-blue-200 rounded-full h-3 mb-2">
                <div className="bg-gradient-to-r from-blue-400 to-blue-500 h-3 rounded-full" style={{ width: '70%' }}></div>
              </div>
              <div className="flex justify-between text-xs text-blue-700">
                <span><strong>Savings:</strong> $300K | <strong>Timeline:</strong> March 2025</span>
                <span>70% Priority</span>
              </div>
            </div>

            {/* Recommendation 3 - Horizontal Bar */}
            <div className="bg-gradient-to-r from-orange-50 to-red-50 border border-orange-200 rounded-lg p-4">
              <div className="flex items-center justify-between mb-3">
                <div>
                  <h5 className="font-semibold text-orange-800">Reduce Print Allocation</h5>
                  <p className="text-sm text-orange-700">Cut budget by 40%, reallocate to high-ROI channels</p>
                </div>
                <div className="text-right">
                  <div className="text-lg font-bold text-orange-600">-40%</div>
                  <div className="text-xs text-orange-600">Budget Cut</div>
                </div>
              </div>
              <div className="w-full bg-orange-200 rounded-full h-3 mb-2">
                <div className="bg-gradient-to-r from-orange-400 to-red-500 h-3 rounded-full" style={{ width: '60%' }}></div>
              </div>
              <div className="flex justify-between text-xs text-orange-700">
                <span><strong>Reallocation:</strong> $840K | <strong>Timeline:</strong> Q3 2025</span>
                <span>60% Priority</span>
              </div>
            </div>
          </div>
        </div>

      </CardContent>
    </Card>
  );
};

export default WaterfallChart;